// src/data/facilities.ts
import type { Language } from "@/lib/i18n";
import type { IconType } from "react-icons";
import {
  HiWifi,
  HiShieldCheck,
  HiUsers,
} from "react-icons/hi";
import {
  IoCarSport,
  IoCafe,
  IoShirt,
  IoFlash,
  IoTv,
  IoSparkles,
  IoBook,
  IoWater,
} from "react-icons/io5";
import { GiCookingPot, GiMuscleUp } from "react-icons/gi";
import { MdOutlinePark } from "react-icons/md";

// Types
interface FacilityHomeItem {
  id: number;
  title: string;
  desc: string;
  icon: IconType;
}

interface FacilitySpotlight {
  id: number;
  title: string;
  desc: string;
  image: string;
  features: string[];
}

interface AmenityItem {
  icon: IconType;
  title: string;
  desc: string;
}

// Icons are language-independent
const FACILITY_ICONS = {
  wifi: HiWifi,
  security: HiShieldCheck,
  parking: IoCarSport,
  lounge: HiUsers,
  kitchen: GiCookingPot,
  laundry: IoShirt,
} as const;

// Home list data
const facilitiesHomeByLang: Record<Language, FacilityHomeItem[]> = {
  id: [
    {
      id: 1,
      title: "Premium High-Speed WiFi",
      desc: "Internet fiber optik dedicated 200Mbps untuk produktivitas tanpa hambatan, support video conference dan streaming 4K.",
      icon: HiWifi,
    },
    {
      id: 2,
      title: "24/7 Concierge & Security",
      desc: "Layanan concierge 24 jam plus sistem keamanan multi-layer dengan CCTV 360° dan akses kartu digital terenkripsi.",
      icon: HiShieldCheck,
    },
    {
      id: 3,
      title: "Exclusive Underground Parking",
      desc: "Area parkir bawah tanah yang aman dan teduh untuk mobil dan motor, dengan charging station untuk kendaraan listrik.",
      icon: IoCarSport,
    },
    {
      id: 4,
      title: "Executive Lounge & Co-Working",
      desc: "Ruang kerja premium dengan private booths, meeting rooms, dan free specialty coffee untuk produktivitas maksimal.",
      icon: HiUsers,
    },
    {
      id: 5,
      title: "Gourmet Kitchen & Dining",
      desc: "Dapur komunitas dengan peralatan chef-grade, island counter, dan area makan yang elegan untuk social dining.",
      icon: GiCookingPot,
    },
    {
      id: 6,
      title: "Premium Laundry & Valet",
      desc: "Layanan laundry valet dengan dry cleaning option, ironing service, dan pengembalian express dalam 24 jam.",
      icon: IoShirt,
    },
  ],
  en: [
    {
      id: 1,
      title: "Premium High-Speed WiFi",
      desc: "Dedicated 200Mbps fiber optic internet for uninterrupted productivity, supporting video conferencing and 4K streaming.",
      icon: HiWifi,
    },
    {
      id: 2,
      title: "24/7 Concierge & Security",
      desc: "Round-the-clock concierge service plus multi-layer security system with 360° CCTV and encrypted digital card access.",
      icon: HiShieldCheck,
    },
    {
      id: 3,
      title: "Exclusive Underground Parking",
      desc: "Safe and shaded underground parking for cars and motorcycles, with charging stations for electric vehicles.",
      icon: IoCarSport,
    },
    {
      id: 4,
      title: "Executive Lounge & Co-Working",
      desc: "Premium workspace with private booths, meeting rooms, and complimentary specialty coffee for maximum productivity.",
      icon: HiUsers,
    },
    {
      id: 5,
      title: "Gourmet Kitchen & Dining",
      desc: "Community kitchen with chef-grade equipment, island counter, and elegant dining area for social dining.",
      icon: GiCookingPot,
    },
    {
      id: 6,
      title: "Premium Laundry & Valet",
      desc: "Valet laundry service with dry cleaning option, ironing service, and express return within 24 hours.",
      icon: IoShirt,
    },
  ],
};

// Spotlight data
const facilitiesSpotlightByLang: Record<Language, FacilitySpotlight[]> = {
  id: [
    {
      id: 1,
      title: "KinKen Executive Lounge & Co-Working Space",
      desc: "Lebih dari sekadar ruang tunggu, ini adalah kantor kedua Anda. Area premium dengan private booths, high-speed internet dedicated, power outlets di setiap seat, dan free-flow specialty coffee dari local roastery. Perfect untuk remote workers, entrepreneurs, dan digital nomads.",
      image: "/images/facilities/lounge-facility.webp",
      features: [
        "Private Soundproof Booths",
        "Meeting Room (4-6 persons)",
        "Free Specialty Coffee Bar",
        "High-Speed Fiber Optic",
        "Print & Scan Station",
      ],
    },
    {
      id: 2,
      title: "Gourmet Community Kitchen & Dining Area",
      desc: "Dapur komunitas dengan standar restoran premium. Dilengkapi peralatan chef-grade, island counter untuk social cooking, dan dining area yang dirancang untuk interaksi yang meaningful. Cocok untuk dinner parties kecil atau cooking session bersama teman se-unit.",
      image: "/images/facilities/kitchen-facility.webp",
      features: [
        "Chef-Grade Appliances",
        "Induction Cooktops",
        "Large Commercial Refrigerator",
        "Wine & Beverage Cooler",
        "Complete Cooking Utensils Set",
      ],
    },
    {
      id: 3,
      title: "Wellness Pavilion: Gym, Yoga & Meditation Garden",
      desc: "Wellness center eksklusif yang mengintegrasikan fitness, mindfulness, dan nature. Selain peralatan gym premium, kami menyediakan yoga studio dengan instructor certified dan meditation garden dengan suara air yang menenangkan.",
      image: "/images/facilities/wellness-facility.webp",
      features: [
        "Premium Cardio Machines",
        "Free Weights Area",
        "Yoga Studio with Instructor",
        "Meditation Garden",
        "Sauna & Steam Room",
      ],
    },
  ],
  en: [
    {
      id: 1,
      title: "KinKen Executive Lounge & Co-Working Space",
      desc: "More than just a waiting area, this is your second office. Premium space with private booths, dedicated high-speed internet, power outlets at every seat, and free-flow specialty coffee from local roastery. Perfect for remote workers, entrepreneurs, and digital nomads.",
      image: "/images/facilities/lounge-facility.webp",
      features: [
        "Private Soundproof Booths",
        "Meeting Room (4-6 persons)",
        "Free Specialty Coffee Bar",
        "High-Speed Fiber Optic",
        "Print & Scan Station",
      ],
    },
    {
      id: 2,
      title: "Gourmet Community Kitchen & Dining Area",
      desc: "Community kitchen with premium restaurant standards. Equipped with chef-grade appliances, island counter for social cooking, and dining area designed for meaningful interactions. Perfect for small dinner parties or cooking sessions with fellow residents.",
      image: "/images/facilities/kitchen-facility.webp",
      features: [
        "Chef-Grade Appliances",
        "Induction Cooktops",
        "Large Commercial Refrigerator",
        "Wine & Beverage Cooler",
        "Complete Cooking Utensils Set",
      ],
    },
    {
      id: 3,
      title: "Wellness Pavilion: Gym, Yoga & Meditation Garden",
      desc: "Exclusive wellness center integrating fitness, mindfulness, and nature. Besides premium gym equipment, we provide yoga studio with certified instructors and meditation garden with soothing water sounds.",
      image: "/images/facilities/wellness-facility.webp",
      features: [
        "Premium Cardio Machines",
        "Free Weights Area",
        "Yoga Studio with Instructor",
        "Meditation Garden",
        "Sauna & Steam Room",
      ],
    },
  ],
};

// All amenities (icons are language-independent)
const allAmenitiesByLang: Record<Language, AmenityItem[]> = {
  id: [
    { icon: HiWifi, title: "Premium WiFi", desc: "200Mbps Dedicated Fiber" },
    { icon: HiShieldCheck, title: "24/7 Concierge", desc: "Asisten Personal" },
    { icon: IoCarSport, title: "Parkir Bawah Tanah", desc: "EV Charging Tersedia" },
    { icon: IoCafe, title: "Specialty Coffee Bar", desc: "Kopi Gratis Pagi" },
    { icon: IoShirt, title: "Valet Laundry", desc: "Dry Cleaning Termasuk" },
    { icon: GiMuscleUp, title: "Wellness Pavilion", desc: "Gym & Yoga Studio" },
    { icon: GiCookingPot, title: "Dapur Gourmet", desc: "Peralatan Chef-Grade" },
    { icon: IoFlash, title: "Backup Power", desc: "Generator & UPS System" },
    { icon: IoTv, title: "Entertainment Hub", desc: "Netflix & Apple TV" },
    { icon: IoSparkles, title: "Housekeeping Harian", desc: "Premium Cleaning" },
    { icon: IoBook, title: "Perpustakaan", desc: "Buku & Majalah" },
    { icon: MdOutlinePark, title: "Taman Tropis", desc: "Ruang Meditasi" },
    { icon: IoWater, title: "Rooftop Lounge", desc: "Sunset View Denpasar" },
    { icon: HiUsers, title: "Event Komunitas", desc: "Social Mixer Bulanan" },
  ],
  en: [
    { icon: HiWifi, title: "Premium WiFi", desc: "200Mbps Dedicated Fiber" },
    { icon: HiShieldCheck, title: "24/7 Concierge", desc: "Personal Assistance" },
    { icon: IoCarSport, title: "Underground Parking", desc: "EV Charging Available" },
    { icon: IoCafe, title: "Specialty Coffee Bar", desc: "Free Morning Brew" },
    { icon: IoShirt, title: "Valet Laundry", desc: "Dry Cleaning Included" },
    { icon: GiMuscleUp, title: "Wellness Pavilion", desc: "Gym & Yoga Studio" },
    {
      icon: GiCookingPot,
      title: "Gourmet Kitchen",
      desc: "Chef-Grade Equipment",
    },
    { icon: IoFlash, title: "Backup Power", desc: "Generator & UPS System" },
    { icon: IoTv, title: "Entertainment Hub", desc: "Netflix & Apple TV" },
    { icon: IoSparkles, title: "Daily Housekeeping", desc: "Premium Cleaning" },
    { icon: IoBook, title: "Curated Library", desc: "Books & Magazines" },
    { icon: MdOutlinePark, title: "Tropical Garden", desc: "Meditation Space" },
    { icon: IoWater, title: "Rooftop Lounge", desc: "Sunset View Denpasar" },
    { icon: HiUsers, title: "Community Events", desc: "Monthly Social Mixer" },
  ],
};

// Backward compatibility exports
export const FACILITIES_HOME_LIST = facilitiesHomeByLang.id;
export const FACILITIES_PAGE_SPOTLIGHT = facilitiesSpotlightByLang.id;
export const ALL_AMENITIES_LIST = allAmenitiesByLang.id;

// i18n functions
export function getFacilitiesHomeList(
  lang: Language = "id",
): FacilityHomeItem[] {
  return facilitiesHomeByLang[lang] || facilitiesHomeByLang.id;
}

export function getFacilitiesSpotlight(
  lang: Language = "id",
): FacilitySpotlight[] {
  return facilitiesSpotlightByLang[lang] || facilitiesSpotlightByLang.id;
}

export function getAllAmenitiesList(lang: Language = "id"): AmenityItem[] {
  return allAmenitiesByLang[lang] || allAmenitiesByLang.id;
}
